import type { Container } from "pixi.js";
import type { Game } from "@engine/core/Game";

export interface PerfStats {
  fps: number;
  msFrame: number;
  entities: number;
  displayObjects: number;
}

const SAMPLE_INTERVAL_S = 0.25;

/**
 * Caches frame-cost metrics so the HUD can read them cheaply.
 * Re-samples every 250ms to avoid traversing the scene graph each frame.
 */
export class PerfMonitor {
  private accumulator = 0;
  private cached: PerfStats = {
    fps: 0,
    msFrame: 0,
    entities: 0,
    displayObjects: 0,
  };
  private entityProvider: () => number = () => 0;

  constructor(private readonly game: Game) {}

  /** Scenes hand a thunk that returns the live ECS entity count. */
  setEntityProvider(fn: () => number): void {
    this.entityProvider = fn;
  }

  clearEntityProvider(): void {
    this.entityProvider = () => 0;
  }

  /** Drive from the game loop. dt in seconds. */
  update(dt: number): void {
    this.accumulator += dt;
    if (this.accumulator < SAMPLE_INTERVAL_S) return;
    this.accumulator = 0;
    const ticker = this.game.app.ticker;
    this.cached = {
      fps: Math.round(ticker.FPS),
      msFrame: Math.round(ticker.deltaMS * 100) / 100,
      entities: this.entityProvider(),
      displayObjects:
        countChildrenDeep(this.game.layers.worldRoot) +
        countChildrenDeep(this.game.layers.hudRoot),
    };
  }

  getStats(): PerfStats {
    return this.cached;
  }
}

function countChildrenDeep(c: Container): number {
  let n = c.children.length;
  for (const child of c.children) n += countChildrenDeep(child as Container);
  return n;
}
